import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Phone, CheckCircle2, Heart, Trash2, FileDown, ShieldAlert, ShieldCheck, Pencil } from "lucide-react";
import { maybeSubscribePush } from "../utils/pwa";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import KycModal from "../components/KycModal";
import PaymentMethodDialog from "../components/PaymentMethodDialog";
import PhoneGateModal from "../components/PhoneGateModal";
import PhoneInput from "../components/PhoneInput";
import ReferralTab from "../components/ReferralTab";
import AccountTabs from "../components/AccountTabs";
import DealerApiPanel from "../components/DealerApiPanel";
import { EsimCard } from "../components/EsimCard";
import SimsConsole from "./SimsConsole";
import ApiAccessCard from "../components/ApiAccessCard";
import KycReminderBanner from "../components/KycReminderBanner";
import Turnstile, { turnstileActive } from "../components/Turnstile";
import { useHoneypot } from "../components/Honeypot";
import { Reveal } from "../components/Reveal";
import { IdentityVerificationCard } from "../components/IdentityVerificationCard";
import { TwoFactorStep } from "../components/TwoFactorStep";
import { TwoFactorSetup } from "../components/TwoFactorSetup";
import { TwoFactorCard } from "../components/TwoFactorCard";

function AuthForms() {
  const { t, fmt, currency, lang } = useLanguage();
  const { loginEmail, registerEmail, completeTwoFactor } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [bonusCode, setBonusCode] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  // 2FA: {kind: "verify", preToken, methods} oppure {kind: "setup", preToken} (admin obbligato)
  const [twofa, setTwofa] = useState(null);
  const hp = useHoneypot();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const afterAuth = (u) => {
    const next = params.get("next");
    // admin: solo dashboard operativa, niente area cliente pubblica
    if (u?.is_admin && !next) {
      navigate("/admin", { replace: true });
      return;
    }
    navigate(next || `/account${params.get("tab") ? `?tab=${params.get("tab")}` : ""}`, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && turnstileActive() && !captcha) {
      toast.error(t("misc.captcha"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await loginEmail(email, password, lang);
        if (res?.requires_2fa) {
          setTwofa({ kind: "verify", preToken: res.pre_token, methods: res.methods });
          return;
        }
        if (res?.requires_2fa_setup) {
          setTwofa({ kind: "setup", preToken: res.pre_token });
          return;
        }
        afterAuth(res);
      } else {
        const u = await registerEmail(name, email, password, terms, lang, captcha, hp.payload(), { age: ageOk, marketing: marketingOptIn, bonus: bonusCode });
        if (u?.bonus_credited) toast.success(t("wallet.bonusOk").replace("{a}", u.bonus_credited));
        afterAuth(u);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.startsWith("login_locked")) {
        const mins = Math.ceil((parseInt(detail.split(":")[1] || "1800", 10)) / 60);
        toast.error(t("tfa.loginLocked").replace("{min}", mins));
      } else if (detail === "account_locked_reset_required") {
        toast.error(t("tfa.accountLocked"));
      } else if (detail === "age_confirmation_required") {
        toast.error(t("auth.ageRequired"));
      } else {
        toast.error(detail.startsWith("security_check") ? t("misc.captcha") : detail || t("misc.error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // OAuth Google DIRETTO (client del titolare): redirect a Google, ritorno su /auth/google/callback
    const next = params.get("next");
    try {
      const { data: cfg } = await api.get("/config");
      if (!cfg.google_client_id) throw new Error("google_not_configured");
      const state = crypto.randomUUID();
      sessionStorage.setItem("gstate", state);
      if (next) sessionStorage.setItem("gnext", next);
      const q = new URLSearchParams({
        client_id: cfg.google_client_id,
        redirect_uri: window.location.origin + "/auth/google/callback",
        response_type: "code",
        scope: "openid email profile",
        state,
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${q.toString()}`;
    } catch {
      toast.error(t("misc.error"));
    }
  };

  return (
    <div data-testid="auth-forms" className="max-w-md mx-auto py-16 px-4">
      <Reveal>
        <div className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-5 sm:p-10">
          <h1 className="font-display font-black text-3xl tracking-tight">{t("auth.welcome")}</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">{t("auth.sub")}</p>

          {!twofa && (
          <>
          <button data-testid="google-login-button" onClick={googleLogin}
            className="mt-8 w-full rounded-full border border-[var(--border-strong)] px-6 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("auth.google")}
          </button>

          <div className="my-6 flex items-center gap-4">
            <span className="flex-1 h-px bg-[var(--border-subtle)]" />
            <span className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">email</span>
            <span className="flex-1 h-px bg-[var(--border-subtle)]" />
          </div>
          </>
          )}

          {twofa?.kind === "setup" ? (
            <>
              <h2 className="font-display font-bold text-xl mb-4">{t("tfa.setupTitle")}</h2>
              <TwoFactorSetup preToken={twofa.preToken} forced
                onDone={(data) => { if (data?.token) afterAuth(completeTwoFactor(data)); setTwofa(null); }}
                onCancel={() => setTwofa(null)} />
            </>
          ) : twofa?.kind === "verify" ? (
            <>
              <h2 className="font-display font-bold text-xl mb-4">{t("tfa.title")}</h2>
              <TwoFactorStep preToken={twofa.preToken} methods={twofa.methods}
                onSuccess={(data) => { const u = completeTwoFactor(data); setTwofa(null); afterAuth(u); }}
                onCancel={() => setTwofa(null)} />
            </>
          ) : mode === "forgot" ? (
            <RecoveryFlow />
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("auth.name")} className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors" />
            )}
            <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.email")} className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors" />
            <input data-testid="auth-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.password")} className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors" />
            {mode === "register" && (
              <input data-testid="auth-bonus-input" type="text" value={bonusCode}
                onChange={(e) => setBonusCode(e.target.value)} placeholder={t("auth.bonusCode")}
                className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors font-mono-t tracking-wider" />
            )}
            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer leading-relaxed">
                <input data-testid="auth-terms-checkbox" type="checkbox" required checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF6B35] shrink-0" />
                <span>
                  {t("auth.accept")}{" "}
                  <Link to="/legal/terms" data-testid="auth-terms-link" target="_blank" className="text-[#FF6B35] font-medium hover:underline">{t("legal.terms")}</Link>{" "}
                  {t("auth.and")}{" "}
                  <Link to="/legal/privacy" data-testid="auth-privacy-link" target="_blank" className="text-[#FF6B35] font-medium hover:underline">{t("legal.privacy")}</Link>
                </span>
              </label>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer leading-relaxed">
                <input data-testid="auth-age-checkbox" type="checkbox" required checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF6B35] shrink-0" />
                <span>{t("auth.ageDeclaration")}</span>
              </label>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer leading-relaxed">
                <input data-testid="auth-marketing-checkbox" type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF6B35] shrink-0" />
                <span>{t("auth.marketingOptIn")}</span>
              </label>
            )}
            {mode === "register" && <Turnstile onToken={setCaptcha} />}
            <hp.Field />
            <button data-testid="auth-submit-button" type="submit" disabled={busy}
              className="w-full rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? t("auth.login") : mode === "register" ? t("auth.register") : t("auth.sendLink")}
            </button>
          </form>
          )}

          {!twofa && mode === "login" && (
            <button data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setResetSent(false); }}
              className="mt-4 w-full text-center text-sm text-[#FF6B35] hover:underline">
              {t("auth.forgot")}
            </button>
          )}

          {!twofa && (
          <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setResetSent(false); }}
            className="mt-6 w-full text-center text-sm text-[var(--text-secondary)] hover:text-[var(--accent-terracotta)] transition-colors">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
          </button>
          )}
        </div>
      </Reveal>
    </div>
  );
}

function TrustedDevicesCard() {
  const { t, lang } = useLanguage();
  const [devices, setDevices] = useState(null);
  const load = () => api.get("/phone/trusted-devices").then(({ data }) => setDevices(data.devices || [])).catch(() => setDevices([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (id) => {
    try {
      await api.delete(`/phone/trusted-devices/${id}`);
      const cur = (localStorage.getItem("simamma_td") || "").split("_")[1];
      if (cur && cur === id) localStorage.removeItem("simamma_td");
      toast.success(t("td.revoked"));
      load();
    } catch { toast.error(t("misc.error")); }
  };

  if (!devices) return null;
  const fmtD = (d) => { try { return new Date(d).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" }); } catch { return d; } };

  return (
    <div data-testid="trusted-devices-card" className="mt-8 rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8">
      <div className="flex items-center gap-2 font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-terracotta)] mb-2">
        <ShieldCheck className="w-3.5 h-3.5" /> {t("td.title")}
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-5">{t("td.sub")}</p>
      {devices.length === 0 ? (
        <p data-testid="trusted-devices-empty" className="text-sm text-[var(--text-secondary)]">{t("td.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {devices.map((d) => (
            <li key={d.token_id} data-testid={`trusted-device-${d.token_id}`} className="flex items-center justify-between gap-4 rounded-2xl border border-[var(--border-subtle)] px-5 py-3">
              <div>
                <div className="font-display font-bold text-sm">{d.device_name}</div>
                <div className="font-mono-t text-[11px] text-[var(--text-secondary)] mt-0.5">
                  {t("td.added")} {fmtD(d.created_at)} · {t("td.lastUsed")} {fmtD(d.last_used_at)} · {t("td.expires")} {fmtD(d.expires_at)}
                </div>
              </div>
              <button data-testid={`trusted-device-revoke-${d.token_id}`} onClick={() => revoke(d.token_id)}
                className="rounded-full border border-[var(--border-strong)] px-4 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] hover:border-red-400 hover:text-red-500 transition-colors">
                {t("td.revoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function PhoneVerifyCard() {
  const { t } = useLanguage();
  const { user, checkAuth } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setEnabled(!!data.phone_verification)).catch(() => {});
  }, []);

  if (!enabled) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/phone/send", { phone });
      setStep(2);
      toast.success(t("acc.phoneSent"));
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "phone_trial_unverified") toast.error(t("acc.phoneFailed"));
      else if (detail === "send_failed") toast.error(t("acc.phoneError"));
      else if (detail === "too_many_attempts") toast.error(t("misc.error"));
      else toast.error(t("acc.phoneInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/phone/verify", { phone, code });
      await checkAuth();
      toast.success(t("acc.phoneVerified"));
    } catch {
      toast.error(t("acc.phoneInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "flex-1 rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors";
  const btnCls = "rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2";

  if (user?.phone_verified) {
    return (
      <div data-testid="phone-verified-badge" className="mt-8 flex items-center gap-4 rounded-3xl border border-emerald-200 bg-emerald-50 px-6 py-4">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
        <div>
          <div className="font-display font-bold text-emerald-700">{t("acc.phoneVerified")}</div>
          <div className="font-mono-t text-xs text-emerald-600 mt-0.5">{user.phone}</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="phone-card" className="mt-8 rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8">
      <div className="flex items-center gap-2 font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-terracotta)] mb-2">
        <Phone className="w-3.5 h-3.5" /> {t("acc.phoneTitle")}
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-5">{t("acc.phoneSub")}</p>
      {step === 1 ? (
        <div className="flex flex-col sm:flex-row gap-3">
          <PhoneInput testid="phone-input" onChange={setPhone}
            className="flex-1 rounded-2xl border border-[var(--border-strong)] bg-transparent focus-within:border-[var(--accent-terracotta)] transition-colors" />
          <button data-testid="phone-send" onClick={send} disabled={busy || phone.length < 8} className={btnCls}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t("acc.phoneSend")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="phone-code-input" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder={t("acc.phoneCode")} className={`${inputCls} font-mono-t tracking-[0.3em]`} />
          <button data-testid="phone-verify-submit" onClick={verify} disabled={busy || code.length < 4} className={btnCls}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t("acc.phoneVerify")}
          </button>
          <button data-testid="phone-resend" onClick={() => { setStep(1); setCode(""); }}
            className="rounded-full border border-[var(--border-strong)] px-6 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
            {t("acc.phoneResend")}
          </button>
        </div>
      )}
    </div>
  );
}

function RecoveryFlow() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [options, setOptions] = useState(null);
  const [method, setMethod] = useState(null);
  const [code, setCode] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [proof, setProof] = useState("");        // secondo dato: codice fiscale o cellulare
  const [emailStep, setEmailStep] = useState(false); // true = mostra il campo secondo dato
  const [blocked, setBlocked] = useState(false);     // account non verificabile → supporto
  const hp = useHoneypot();

  const inputCls = "w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors";
  const btnCls = "w-full rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2";

  const getOptions = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/options", { email, ...hp.payload() });
      setOptions(data.data);
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async (e) => {
    e?.preventDefault();
    if (turnstileActive() && !captcha) {
      toast.error(t("misc.captcha"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, proof, turnstile_token: captcha, ...hp.payload() });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail === "recovery_unverifiable") {
        setBlocked(true);
      } else if (detail === "proof_mismatch" || detail === "proof_required") {
        toast.error(t("rec.proofWrong"));
      } else if (detail.startsWith("proof_locked") || detail === "too_many_attempts") {
        toast.error(t("rec.proofLocked"));
      } else {
        toast.error(detail.startsWith("security_check") ? t("misc.captcha") : t("misc.error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const sendSms = async () => {
    setBusy(true);
    try {
      await api.post("/auth/recovery/sms", { email });
      setMethod("sms");
      toast.success(t("acc.phoneSent"));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(t(d === "phone_trial_unverified" ? "acc.phoneFailed" : d === "send_failed" ? "acc.phoneError" : "misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const verifySms = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/sms/verify", { email, code });
      navigate(`/reset-password?token=${data.data.token}`);
    } catch {
      toast.error(t("acc.phoneInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const verifyQuestions = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/questions", { email, answers });
      navigate(`/reset-password?token=${data.data.token}`);
    } catch {
      toast.error(t("sec.wrong"));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return <div data-testid="forgot-sent" className="rounded-2xl bg-emerald-50 text-emerald-700 px-5 py-4 text-sm leading-relaxed">{t("auth.resetSent")}</div>;
  }

  if (blocked) {
    return (
      <div data-testid="recovery-blocked" className="space-y-4">
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{t("rec.unverifiable")}</p>
        <a data-testid="recovery-mailto" href="mailto:info@vediloo.it?subject=Recupero%20password"
          className={`${btnCls} no-underline`}>
          {t("rec.contactSupport")}
        </a>
        <Link data-testid="recovery-ticket" to="/carta-servizi"
          className="block w-full text-center rounded-full border border-[var(--border-strong)] px-6 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
          {t("rec.openTicket")}
        </Link>
      </div>
    );
  }

  if (!options) {
    return (
      <form onSubmit={getOptions} className="space-y-4">
        <hp.Field />
        <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.email")} className={inputCls} />
        <Turnstile onToken={setCaptcha} />
        <button data-testid="auth-submit-button" type="submit" disabled={busy} className={btnCls}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.sendLink")}
        </button>
      </form>
    );
  }

  return (
    <div data-testid="recovery-methods" className="space-y-3">
      <p className="text-sm text-[var(--text-secondary)] mb-4">{t("rec.choose")}</p>
      <button data-testid="recovery-method-email" onClick={() => setEmailStep(true)} disabled={busy}
        className="w-full rounded-2xl border border-[var(--border-strong)] px-5 py-3.5 text-sm font-medium text-left hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors disabled:opacity-60">
        {t("rec.emailLink")}{options.masked.backup_email ? ` · ${t("rec.backupToo")}` : ""}
      </button>

      {emailStep && (
        <form onSubmit={sendEmail} className="space-y-3 pt-2" data-testid="recovery-proof-form">
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">{t("rec.proofHelp")}</p>
          <input data-testid="recovery-proof-input" required value={proof} onChange={(e) => setProof(e.target.value)}
            placeholder={t("rec.proofLabel")} autoComplete="off" className={inputCls} />
          <button data-testid="recovery-send-email" type="submit" disabled={busy || !proof.trim()} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.sendLink")}
          </button>
        </form>
      )}
      {options.methods.includes("sms") && method !== "sms" && (
        <button data-testid="recovery-method-sms" onClick={sendSms} disabled={busy}
          className="w-full rounded-2xl border border-[var(--border-strong)] px-5 py-3.5 text-sm font-medium text-left hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors disabled:opacity-60">
          {t("rec.smsTo")} {options.masked.phone}
        </button>
      )}
      {options.methods.includes("questions") && method !== "questions" && (
        <button data-testid="recovery-method-questions" onClick={() => setMethod("questions")}
          className="w-full rounded-2xl border border-[var(--border-strong)] px-5 py-3.5 text-sm font-medium text-left hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
          {t("rec.questions")}
        </button>
      )}

      {method === "sms" && (
        <form onSubmit={verifySms} className="space-y-3 pt-2">
          <input data-testid="recovery-code-input" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder={t("acc.phoneCode")} className={`${inputCls} font-mono-t tracking-[0.3em]`} />
          <button data-testid="recovery-verify-sms" type="submit" disabled={busy || code.length < 4} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("rec.verify")}
          </button>
        </form>
      )}

      {method === "questions" && (
        <form onSubmit={verifyQuestions} className="space-y-3 pt-2">
          {(options.masked.question_ids || []).map((qid, i) => (
            <div key={qid}>
              <div className="text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t(`sec.${qid}`)}</div>
              <input data-testid={`recovery-answer-${i}`} value={answers[qid] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [qid]: e.target.value }))}
                placeholder={t("sec.answer")} className={inputCls} />
            </div>
          ))}
          <p className="text-xs text-[var(--text-muted)]">{t("sec.need4")}</p>
          <button data-testid="recovery-verify-questions" type="submit" disabled={busy} className={btnCls}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("rec.verify")}
          </button>
        </form>
      )}
    </div>
  );
}

const QUESTION_IDS = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8"];

function SecurityCard() {
  const { t } = useLanguage();
  const [backup, setBackup] = useState("");
  const [qids, setQids] = useState(["q1", "q2", "q3", "q4", "q5"]);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/account/security").then(({ data }) => {
      setBackup(data.backup_email || "");
      if (data.question_ids?.length === 5) setQids(data.question_ids);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { backup_email: backup };
      const filled = qids.filter((q) => (answers[q] || "").trim().length >= 2);
      if (filled.length > 0) {
        if (new Set(qids).size !== 5) {
          toast.error(t("sec.distinct"));
          setBusy(false);
          return;
        }
        if (filled.length !== 5) {
          toast.error(t("sec.need5"));
          setBusy(false);
          return;
        }
        payload.answers = Object.fromEntries(qids.map((q) => [q, answers[q].trim()]));
      }
      await api.post("/account/security", payload);
      toast.success(t("sec.saved"));
      setAnswers({});
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors";

  return (
    <div data-testid="security-card" className="mt-8 rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8">
      <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-terracotta)] mb-2">{t("sec.title")}</div>
      <p className="text-sm text-[var(--text-secondary)] mb-5">{t("sec.sub")}</p>

      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t("sec.backup")}</label>
      <input data-testid="backup-email-input" type="email" value={backup} onChange={(e) => setBackup(e.target.value)}
        placeholder="backup@email.com" className={`${inputCls} mb-6`} />

      <div className="text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t("sec.questions")}</div>
      <p className="text-xs text-[var(--text-muted)] mb-3">{t("sec.questionsSub")}</p>
      <div className="space-y-3 mb-6">
        {qids.map((qid, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-2">
            <select data-testid={`sec-q-select-${i}`} value={qid}
              onChange={(e) => setQids((q) => q.map((x, xi) => (xi === i ? e.target.value : x)))}
              className={`${inputCls} bg-[var(--bg-card)]`}>
              {QUESTION_IDS.map((q) => (
                <option key={q} value={q}>{t(`sec.${q}`)}</option>
              ))}
            </select>
            <input data-testid={`sec-q-answer-${i}`} value={answers[qid] || ""}
              onChange={(e) => setAnswers((a) => ({ ...a, [qid]: e.target.value }))}
              placeholder={t("sec.answer")} className={inputCls} />
          </div>
        ))}
      </div>

      <button data-testid="sec-save" onClick={save} disabled={busy}
        className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-7 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center gap-2">
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t("sec.save")}
      </button>
    </div>
  );
}

function PrivacySection({ user, onChanged }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [consents, setConsents] = useState(null);
  const [editName, setEditName] = useState(user?.name || "");
  const [editEmail, setEditEmail] = useState(user?.email || "");
  const [busySave, setBusySave] = useState(false);
  const [busyConsent, setBusyConsent] = useState(false);
  const [busyExport, setBusyExport] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busyDel, setBusyDel] = useState(false);

  useEffect(() => {
    api.get("/gdpr/consents").then(({ data }) => setConsents(data.data)).catch(() => setConsents({ error: true }));
  }, []);

  const toggleMarketing = async () => {
    if (!consents || busyConsent) return;
    setBusyConsent(true);
    try {
      const { data } = await api.post("/gdpr/consents", { kind: "marketing", granted: !consents.marketing?.granted });
      setConsents(data.data);
      toast.success(t("admin.saved"));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusyConsent(false);
    }
  };

  const saveProfile = async () => {
    setBusySave(true);
    try {
      const payload = {};
      if (editName.trim() && editName.trim() !== user.name) payload.name = editName.trim();
      if (editEmail.trim().toLowerCase() !== (user.email || "").toLowerCase()) payload.email = editEmail.trim();
      if (!Object.keys(payload).length) { setBusySave(false); return; }
      const { data } = await api.patch("/auth/profile", payload);
      if (data.email_reverification_sent) toast.success(t("priv.reverifySent"));
      else toast.success(t("admin.saved"));
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      toast.error(detail === "email_taken" ? t("auth.emailTaken") : t("misc.error"));
    } finally {
      setBusySave(false);
    }
  };

  const exportData = async () => {
    setBusyExport(true);
    try {
      const res = await api.get("/gdpr/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "simamma-dati-personali.json"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusyExport(false);
    }
  };

  const deleteAccount = async () => {
    setBusyDel(true);
    try {
      await api.delete("/auth/account");
      toast.success(t("priv.deleteDone"));
      const { clearUserStorage } = await import("../auth");
      clearUserStorage();
      navigate("/", { replace: true });
      window.location.reload();
    } catch {
      toast.error(t("misc.error"));
      setBusyDel(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[var(--accent-terracotta)] transition-colors";
  const marketingOn = consents?.marketing?.granted === true;

  return (
    <div data-testid="privacy-section" className="mt-8 rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8">
      <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-terracotta)] mb-2 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> {t("priv.title")}
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-6">{t("priv.sub")}</p>

      {/* Consenso marketing (separato dai Termini) */}
      <div className="flex items-center gap-3 min-h-[44px] mb-2">
        <button type="button" data-testid="privacy-marketing-toggle" onClick={toggleMarketing} disabled={!consents || busyConsent}
          className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 disabled:opacity-50 ${marketingOn ? "bg-[#FF6B35]" : "bg-slate-300"}`}>
          <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${marketingOn ? "translate-x-5" : ""}`} />
        </button>
        <div>
          <span className="text-sm text-slate-700">{t("priv.marketing")}</span>
          <p className="text-xs text-[var(--text-muted)]">{t("priv.marketingHint")}</p>
        </div>
      </div>

      {/* Rettifica nome/email */}
      <div className="mt-6 mb-2 text-xs font-medium text-[var(--text-secondary)]">{t("priv.profile")}</div>
      <div className="grid sm:grid-cols-2 gap-3 mb-2">
        <input data-testid="privacy-name-input" value={editName} onChange={(e) => setEditName(e.target.value)}
          placeholder={t("auth.name")} className={inputCls} />
        <input data-testid="privacy-email-input" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
          placeholder={t("auth.email")} className={inputCls} />
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">{t("priv.emailNote")}</p>
      <button data-testid="privacy-save-profile" onClick={saveProfile} disabled={busySave}
        className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center gap-2">
        {busySave ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />} {t("sec.save")}
      </button>

      {/* Export dati */}
      <div className="mt-7 pt-5 border-t border-[var(--border-subtle)] flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-medium text-slate-800">{t("priv.export")}</div>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{t("priv.exportHint")}</p>
        </div>
        <button data-testid="privacy-export-button" onClick={exportData} disabled={busyExport}
          className="rounded-full border border-[var(--border-strong)] px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center gap-2">
          {busyExport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} JSON
        </button>
      </div>

      {/* Contatti privacy + policy */}
      <p className="mt-5 text-xs text-[var(--text-muted)]">
        {t("priv.contact")}: <a href="mailto:info@vediloo.it" className="text-[#FF6B35] hover:underline">info@vediloo.it</a>
        {" · "}
        <Link to="/legal/privacy" data-testid="privacy-policy-link" className="text-[#FF6B35] hover:underline">{t("priv.policy")}</Link>
      </p>

      {/* Eliminazione account */}
      <div className="mt-6 rounded-2xl border border-red-200 bg-red-50/50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-medium text-red-700">{t("priv.delete")}</div>
            <p className="text-xs text-red-500/80 mt-0.5">{t("priv.deleteHint")}</p>
          </div>
          {!confirmDel ? (
            <button data-testid="privacy-delete-button" onClick={() => setConfirmDel(true)}
              className="rounded-full border border-red-300 text-red-600 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors flex items-center gap-2">
              <Trash2 className="w-3.5 h-3.5" /> {t("priv.delete")}
            </button>
          ) : (
            <span className="flex items-center gap-2">
              <span className="text-xs text-red-600 font-medium">{t("priv.deleteConfirm")}</span>
              <button data-testid="privacy-delete-confirm" onClick={deleteAccount} disabled={busyDel}
                className="rounded-full bg-red-600 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center gap-2">
                {busyDel && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t("priv.delete")}
              </button>
              <button data-testid="privacy-delete-cancel" onClick={() => setConfirmDel(false)}
                className="rounded-full border border-slate-300 px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-slate-400 transition-colors">
                {t("tfa.cancel")}
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function WalletTab() {
  const { t } = useLanguage();
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const [bonusCode, setBonusCode] = useState("");
  const [bonusBusy, setBonusBusy] = useState(false);
  const pendingAmount = useRef(null);            // { amount, method: "card" | "new_card" }
  const [savedCard, setSavedCard] = useState(null);
  const [topupAmount, setTopupAmount] = useState(null); // importo nel selettore metodo
  const [payBusy, setPayBusy] = useState(null);         // "card" | "new_card" | null
  const [phoneGate, setPhoneGate] = useState(null);     // null | "certify" | "confirm"

  // 428 dal pagamento: telefono/SMS ordine oppure dati fiscali (aziende)
  const handleTopupGate = (e, amount, method) => {
    pendingAmount.current = { amount, method };
    setTopupAmount(null);
    setPayBusy(null);
    const det = e.response?.data?.detail;
    if (det === "phone_required" || det === "order_otp_required") {
      setPhoneGate(det === "phone_required" ? "certify" : "confirm");
    } else {
      setKycOpen(true);
    }
  };

  useEffect(() => {
    api.get("/payments/saved-card").then(({ data }) => setSavedCard(data.data || { saved: false })).catch(() => setSavedCard({ saved: false }));
  }, []);

  const load = (skip = 0) => api.get(`/wallet?skip=${skip}&limit=25`)
    .then(({ data }) => {
      setData((old) => (skip === 0 || !old ? data.data : { ...data.data, transactions: [...old.transactions, ...(data.data.transactions || [])] }));
      setMore(skip + (data.data.transactions || []).length < (data.data.total || 0));
    })
    .catch(() => setData({ balance: 0, transactions: [], total: 0 }));

  useEffect(() => { load(0); }, []);

  const stripeTopup = async (amount) => {
    setBusy(amount);
    try {
      const { data } = await api.post("/wallet/topup", { amount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 428) {
        handleTopupGate(e, amount, "new_card");
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setBusy(null);
      setPayBusy(null);
    }
  };

  // Ricarica veloce: addebito diretto sulla carta salvata, senza lasciare la pagina
  const cardTopup = async (amount) => {
    setPayBusy("card");
    try {
      const { data } = await api.post("/payments/pay-with-saved-card", { amount });
      if (data.status === "requires_action") {
        // 3DS non gestito in-page sul web: fallback al checkout Stripe completo
        toast.info(t("pay.cardAction"));
        setPayBusy(null);
        stripeTopup(amount);
        return;
      }
      toast.success(t("wallet.topupDone"));
      setTopupAmount(null);
      setPayBusy(null);
      load(0);
      checkAuth();
    } catch (e) {
      const st = e.response?.status;
      const det = e.response?.data?.detail;
      if (st === 428) {
        handleTopupGate(e, amount, "card");
      } else if (st === 402) {
        toast.error(t("pay.cardDeclined"));
      } else if (det === "no_saved_card") {
        setSavedCard({ saved: false });
        setTopupAmount(null);
      } else {
        toast.error(det || t("misc.error"));
      }
      setPayBusy(null);
    }
  };

  const startTopup = (amount) => {
    // Con carta salvata il cliente sceglie il metodo; senza, diretto al checkout Stripe
    if (savedCard?.saved) { setTopupAmount(amount); return; }
    stripeTopup(amount);
  };

  const onPickTopup = (method) => {
    const a = topupAmount;
    if (!a) return;
    if (method === "card") cardTopup(a);
    else stripeTopup(a);
  };

  const retryPendingTopup = () => {
    const pend = pendingAmount.current;
    if (!pend) return;
    if (pend.method === "card") cardTopup(pend.amount);
    else stripeTopup(pend.amount);
  };

  const redeemBonus = async () => {
    if (!bonusCode.trim() || bonusBusy) return;
    setBonusBusy(true);
    try {
      const { data } = await api.post("/bonus/redeem", { code: bonusCode.trim() });
      toast.success(t("wallet.bonusOk").replace("{a}", data.data.amount));
      setBonusCode("");
      load(0);
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail === "bonus_code_expired" ? t("wallet.bonusExpired") : t("wallet.bonusInvalid"));
    } finally {
      setBonusBusy(false);
    }
  };

  if (!data) {
    return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  return (
    <div data-testid="account-wallet" className="mt-10 max-w-2xl">
      <div className="rounded-3xl border border-[var(--border-subtle)] bg-white/60 p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("wallet.balance")}</div>
            <div data-testid="wallet-balance" className="mt-1 font-display text-4xl sm:text-5xl font-black text-slate-900">
              € {(data.balance ?? 0).toFixed(2)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[10, 25, 50, 100, 200].map((a) => (
              <button key={a} data-testid={`wallet-topup-${a}`} onClick={() => startTopup(a)} disabled={busy !== null || payBusy !== null}
                className="rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[11px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-50 flex items-center gap-2">
                {busy === a && <Loader2 className="w-3 h-3 animate-spin" />}
                +{a} €
              </button>
            ))}
          </div>
        </div>
        {/* riscatto codice bonus */}
        <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("wallet.haveBonus")}</div>
          <div className="flex gap-2">
            <input data-testid="wallet-bonus-input" type="text" value={bonusCode}
              onChange={(e) => setBonusCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && redeemBonus()}
              placeholder="BONUS-XXXX-XXXX"
              className="flex-1 rounded-full border border-slate-200 bg-transparent px-5 py-2.5 text-sm font-mono-t tracking-wider outline-none focus:border-[#FF6B35] transition-colors" />
            <button data-testid="wallet-bonus-redeem" onClick={redeemBonus} disabled={bonusBusy || !bonusCode.trim()}
              className="rounded-full bg-[#0C0D0E] text-white px-6 py-2.5 font-mono-t text-[11px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-50 flex items-center gap-2">
              {bonusBusy && <Loader2 className="w-3 h-3 animate-spin" />} {t("wallet.redeem")}
            </button>
          </div>
        </div>
      </div>

      <h3 className="mt-8 font-display font-bold text-slate-900">{t("wallet.movements")}</h3>
      <div className="mt-3 space-y-1.5">
        {data.transactions.length === 0 && (
          <p data-testid="wallet-empty" className="py-6 text-center text-sm text-slate-400">{t("admin.noLogs")}</p>
        )}
        {data.transactions.map((m, i) => (
          <div key={m.tx_id || i} data-testid={`wallet-tx-${i}`}
            className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--border-subtle)] bg-white/60 px-5 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{m.note || m.type}</div>
              <div className="font-mono-t text-[10px] text-slate-400">{new Date(m.created_at).toLocaleString()}</div>
            </div>
            <span className={`font-mono-t text-sm font-bold shrink-0 ${m.amount >= 0 ? "text-emerald-600" : "text-slate-900"}`}>
              {m.amount >= 0 ? "+" : ""}{Number(m.amount).toFixed(2)} €
            </span>
          </div>
        ))}
        {more && (
          <div className="pt-2 text-center">
            <button data-testid="wallet-more" onClick={() => load((data.transactions || []).length)}
              className="rounded-full border border-slate-200 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
              {t("admin.showMore")}
            </button>
          </div>
        )}
      </div>
      <PaymentMethodDialog
        open={!!topupAmount}
        onOpenChange={(v) => { if (!v) { setTopupAmount(null); setPayBusy(null); } }}
        title={t("wallet.topup")}
        priceLabel={topupAmount ? `+${topupAmount} €` : ""}
        walletBalance={0}
        card={savedCard}
        busy={payBusy || (topupAmount && busy === topupAmount ? "new_card" : null)}
        onPick={onPickTopup}
      />
      <PhoneGateModal open={!!phoneGate} mode={phoneGate || "confirm"}
        onOpenChange={(v) => { if (!v) setPhoneGate(null); }}
        onDone={() => { setPhoneGate(null); checkAuth(); retryPendingTopup(); }} />
      <KycModal context="topup" open={kycOpen} onOpenChange={setKycOpen} onDone={retryPendingTopup} />
    </div>
  );
}

export default function Account() {
  const { t, fmt, currency, lang } = useLanguage();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [typeBusy, setTypeBusy] = useState(false);
  const [idBusy, setIdBusy] = useState(false);

  // In app installata: registra il dispositivo alle push (avviso giga in esaurimento).
  // Il browser chiede il permesso una sola volta; fuori dall'app non succede nulla.
  useEffect(() => {
    if (user) maybeSubscribePush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const changeAccountType = async (v) => {
    if (typeBusy || v === (user?.kyc_account_type || "private")) return;
    if (v === "business") {
      // Passaggio DEFINITIVO: avviene solo salvando il modulo fiscale completo
      // (ragione sociale + P.IVA + SDI/PEC) — niente cambio con un click.
      toast(t("acc.typeIrreversible"), { icon: "⚠️" });
      setEditKycOpen(true);
      return;
    }
    // business → privato non esiste: il passaggio ad azienda è irreversibile
    toast.error(t("kyc.errIrreversible"));
  };
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "esims");

  // il menu flottante è a link: /account?tab=X deve cambiare il tab attivo (senza ?tab -> eSIM)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get("tab") || "esims";
    if (q !== tab) setTab(q);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const setupSession = searchParams.get("card_setup");
    if (!setupSession || !user) return;
    api.post("/payments/saved-card/complete", { session_id: setupSession })
      .then(() => toast.success(t("auto.cardSaved")))
      .catch(() => toast.error(t("misc.error")))
      .finally(() => navigate("/account?tab=esims", { replace: true }));
  }, [searchParams, user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [esims, setEsims] = useState(null);
  const [orders, setOrders] = useState(null);
  const [wishlist, setWishlist] = useState(null);
  const [editKycOpen, setEditKycOpen] = useState(false);
  // Vista eSIM: normale (card) oppure console tecnica — scelta ricordata sul dispositivo
  const [simView, setSimView] = useState(() => {
    try { return localStorage.getItem("atlas_simview") || "normal"; } catch { return "normal"; }
  });
  const switchSimView = (v) => {
    setSimView(v);
    try { localStorage.setItem("atlas_simview", v); } catch { /* storage non disponibile */ }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/esims").then(({ data }) => setEsims(data.data || [])).catch(() => setEsims([]));
    api.get("/orders").then(({ data }) => setOrders(data.data || [])).catch(() => setOrders([]));
    api.get("/wishlist").then(({ data }) => setWishlist(data.data || [])).catch(() => setWishlist([]));
    // auto-refresh post-acquisto (flag messo da PaymentSuccess): la SIM appare da sola
    // in console entro pochi secondi dal provisioning, senza ricaricare la pagina
    let justBought = 0;
    try { justBought = Number(sessionStorage.getItem("atlas_just_bought") || 0); } catch { /* storage non disponibile */ }
    if (!justBought || Date.now() - justBought > 10 * 60 * 1000) return undefined;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      api.get("/esims").then(({ data }) => setEsims(data.data || [])).catch(() => {});
      api.get("/orders").then(({ data }) => setOrders(data.data || [])).catch(() => {});
      if (tries >= 24) {
        clearInterval(iv);
        try { sessionStorage.removeItem("atlas_just_bought"); } catch { /* storage non disponibile */ }
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [user]);

  const removeWish = async (packageId) => {
    try {
      await api.delete(`/wishlist/${encodeURIComponent(packageId)}`);
      setWishlist((w) => (w || []).filter((x) => x.package_id !== packageId));
      toast.success(t("wish.removed"));
    } catch {
      toast.error(t("misc.error"));
    }
  };

  if (user === undefined) {
    return (
      <div data-testid="account-loading" className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[var(--accent-terracotta)]" />
      </div>
    );
  }
  if (!user) return <AuthForms />;
  // L'admin non ha area cliente pubblica: da /account va dritto alla dashboard operativa
  if (user.is_admin) return <Navigate to="/admin" replace />;

  // Titolo e sottotitolo cambiano col tab attivo (come la Console SIM), mai statici
  const [tabTitle, tabSub] = {
    esims: [t("acc.title"), t("acc.subEsims")],
    wallet: [t("wallet.tab"), t("acc.subWallet")],
    referral: [t("ref.tab"), t("acc.subReferral")],
    api: ["API", t("acc.subApi")],
    account: [t("acc.tabManage"), t("acc.subAccount")],
    security: [t("acc.tabSecurity"), t("acc.subSecurity")],
    orders: [t("acc.tabOrders"), ""],
    wishlist: ["Wishlist", ""],
  }[tab] || [t("acc.title"), t("acc.subEsims")];
  const pendingIdentityOrders = (orders || []).filter((o) => o.status === "pending_identity");

  return (
    <div data-testid="account-dashboard" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-14 sm:py-20">
      <AccountTabs active={tab} />
      <Reveal>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="font-mono-t text-[11px] uppercase tracking-[0.35em] text-[var(--accent-terracotta)]">{user.email}</span>
          <span data-testid="account-role-badge" className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${user.is_admin ? "bg-[#FF6B35] text-white" : "bg-slate-100 text-slate-500"}`}>
            {user.is_admin ? t("acc.roleAdmin") : t("acc.roleUser")}
          </span>
          <span data-testid="account-type-badge" className="rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest bg-slate-100 text-slate-500">
            {user.kyc_account_type === "business" ? t("acc.typeBusiness") : t("acc.typePrivate")}
          </span>
          <span data-testid="account-wallet-badge" className="rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700">
            {t("wallet.tab")} € {Number(user.wallet_balance || 0).toFixed(2)}
          </span>
        </div>
        <h1 data-testid="account-tab-title" className="font-display font-black tracking-tight text-3xl sm:text-6xl">{tabTitle}</h1>
        {tabSub && <p data-testid="account-tab-sub" className="mt-3 text-base text-slate-500 max-w-2xl">{tabSub}</p>}
      </Reveal>

      {(user.is_dealer || user.kyc_account_type === "business") && !user.kyc_done && (
        <KycReminderBanner onOpen={() => setEditKycOpen(true)} />
      )}

      {user.identity_ok === false && (
        <div data-testid="identity-banner" className="mt-6 flex flex-wrap items-center gap-4 rounded-3xl border border-violet-200 bg-violet-50 px-6 py-5">
          <ShieldAlert className="w-6 h-6 text-violet-500 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-slate-900">{t("ver.banner")}</div>
            <div className="text-sm text-slate-600 mt-0.5">{t("ver.bannerSub")}</div>
          </div>
          {!user.email_verified && user.auth_provider !== "google" && (
            <button data-testid="identity-resend" disabled={idBusy}
              onClick={async () => {
                setIdBusy(true);
                try {
                  await api.post("/auth/verify-email/resend");
                  toast.success(t("ver.resent"));
                } catch { toast.error(t("misc.error")); } finally { setIdBusy(false); }
              }}
              className="rounded-full bg-violet-600 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-violet-700 transition-colors disabled:opacity-60 flex items-center gap-2">
              {idBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t("ver.resend")}
            </button>
          )}
        </div>
      )}

      {pendingIdentityOrders.length > 0 && (
        <div data-testid="order-identity-hold-banner" className="mt-6 rounded-3xl border border-violet-200 bg-violet-50 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <ShieldAlert className="w-6 h-6 text-violet-500 shrink-0" />
              <div>
                <div className="font-display font-bold text-slate-900">{t("id.holdTitle")}</div>
                <p className="mt-1 text-sm text-slate-600">{t("id.holdSub").replace("{n}", pendingIdentityOrders.length)}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/account?tab=account" data-testid="order-identity-reupload-button"
                className="rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors">
                {t("id.holdDocs")}
              </Link>
              <Link to="/account?tab=security" data-testid="order-identity-verify-sms-button"
                className="rounded-full border border-violet-300 bg-white text-violet-700 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                {t("id.holdSms")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {tab === "esims" && (
        <div className="mt-10">
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {[["normal", "acc.viewNormal"], ["console", "acc.viewConsole"]].map(([v, key]) => (
              <button key={v} data-testid={`simview-${v}`} onClick={() => switchSimView(v)}
                className={`rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                  simView === v ? "bg-[#1E293B] text-white" : "border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
                {t(key)}
              </button>
            ))}
          </div>
          {simView === "console" ? (
            <SimsConsole embedded />
          ) : (
          <div className="grid gap-6 lg:grid-cols-2">
          {esims === null ? (
            <div className="py-20 text-center lg:col-span-2"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[var(--accent-terracotta)]" /></div>
          ) : esims.length === 0 ? (
            <div data-testid="esims-empty" className="lg:col-span-2 rounded-3xl border border-dashed border-[var(--border-strong)] py-20 text-center">
              <h3 className="font-display font-bold text-2xl">{t("acc.none")}</h3>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">{t("acc.noneSub")}</p>
              <Link to="/destinations" data-testid="esims-browse-link" className="inline-block mt-6 rounded-full bg-[var(--accent-terracotta)] text-white px-8 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--bg-dark)] transition-colors">
                {t("acc.browse")}
              </Link>
            </div>
          ) : (
            esims.map((sim) => <EsimCard key={sim.iccid} sim={sim} />)
          )}
          </div>
          )}
        </div>
      )}

      {tab === "wallet" && <div className="mt-10"><WalletTab /></div>}

      {tab === "referral" && <div className="mt-10"><ReferralTab /></div>}

      {tab === "api" && (user?.is_dealer || user?.api_enabled) && <div className="mt-10"><DealerApiPanel user={user} /></div>}

      {tab === "orders" && (
        <div className="mt-10">
          {orders === null ? (
            <div className="py-20 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[var(--accent-terracotta)]" /></div>
          ) : orders.length === 0 ? (
            <div data-testid="orders-empty" className="rounded-3xl border border-dashed border-[var(--border-strong)] py-20 text-center text-[var(--text-secondary)]">{t("acc.none")}</div>
          ) : (
            <div data-testid="orders-list" className="divide-y divide-[var(--border-subtle)] rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)]">
              {orders.map((o) => (
                <div key={o.order_id} data-testid={`order-row-${o.order_id}`} className="flex flex-wrap items-center justify-between gap-4 px-6 sm:px-8 py-5">
                  <div>
                    <div className="font-display font-bold">{o.package_title}</div>
                    <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mt-1">
                      {o.code} · {new Date(o.created_at).toLocaleDateString()} {o.mock && `· ${t("acc.demo")}`}
                    </div>
                    {o.delivery_pending && o.deliver_at && (
                      <span data-testid={`order-scheduled-${o.order_id}`} className="mt-2 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                        {t("acc.qrScheduled").replace("{date}", new Date(o.deliver_at).toLocaleDateString(lang))}
                      </span>
                    )}
                    {o.departure_date && (() => {
                      const days = Math.ceil((new Date(o.departure_date) - new Date()) / 86400000);
                      const retPast = o.return_date && new Date(o.return_date) < new Date();
                      let badge = null;
                      if (days > 1) badge = t("acc.departIn").replace("{n}", days);
                      else if (days === 1) badge = t("acc.departTomorrow");
                      else if (days === 0) badge = t("acc.departToday");
                      else if (!retPast) badge = t("acc.onTrip");
                      return (
                        <div data-testid={`order-trip-${o.order_id}`} className="mt-2 flex flex-wrap items-center gap-2">
                          {badge && (
                            <span className="rounded-full bg-[#0D9488]/10 text-[#0D9488] px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest">{badge}</span>
                          )}
                          <span className="font-mono-t text-[10px] text-slate-400 uppercase tracking-widest">
                            {new Date(o.departure_date).toLocaleDateString(lang)}{o.return_date ? ` → ${new Date(o.return_date).toLocaleDateString(lang)}` : ""}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-4">
                    {o.fic_invoice_id && (
                      <button data-testid={`order-invoice-${o.order_id}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/orders/${o.order_id}/invoice.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `SIMAMMA-fattura-${o.fic_invoice_number || o.order_id}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch { toast.error(t("misc.error")); }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-strong)] px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-[var(--text-secondary)] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <FileDown className="w-3.5 h-3.5" /> {t("acc.invoice")} {o.fic_invoice_number || ""}
                      </button>
                    )}
                    <span className={`font-mono-t text-[10px] uppercase tracking-[0.2em] ${o.status === "completed" ? "text-[var(--status-active-green)]" : "text-[var(--status-warning-amber)]"}`}>
                      {o.status}
                    </span>
                    <span className="font-mono-t text-sm">{fmt(o.price)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "wishlist" && (
        <div className="mt-10">
          {wishlist === null ? (
            <div className="py-20 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[var(--accent-terracotta)]" /></div>
          ) : wishlist.length === 0 ? (
            <div data-testid="wishlist-empty" className="rounded-3xl border border-dashed border-[var(--border-strong)] py-20 text-center">
              <Heart className="w-8 h-8 mx-auto text-[var(--text-muted)] mb-4" />
              <p className="text-sm text-[var(--text-secondary)]">{t("wish.empty")}</p>
            </div>
          ) : (
            <div data-testid="wishlist-list" className="divide-y divide-[var(--border-subtle)] rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)]">
              {wishlist.map((w) => (
                <div key={w.package_id} data-testid={`wishlist-row-${w.package_id}`} className="flex flex-wrap items-center justify-between gap-4 px-6 sm:px-8 py-5">
                  <div>
                    <div className="font-display font-bold">{w.title}</div>
                    <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mt-1">{w.destination}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono-t text-sm font-bold text-[var(--accent-terracotta)]">{w.current_price != null ? fmt(w.current_price) : "—"}</div>
                      {w.price_at_add != null && w.current_price != null && w.current_price !== w.price_at_add && (
                        <div className="font-mono-t text-[10px] text-[var(--text-muted)] line-through">{fmt(w.price_at_add)}</div>
                      )}
                    </div>
                    <Link to={w.country_code ? `/destination/${w.country_code}` : "/destinations"} data-testid={`wishlist-go-${w.package_id}`}
                      className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-5 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
                      {t("wish.view")}
                    </Link>
                    <button data-testid={`wishlist-remove-${w.package_id}`} onClick={() => removeWish(w.package_id)}
                      aria-label={t("wish.removed")}
                      className="text-[var(--text-muted)] hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "account" && (
        <div className="mt-10" data-testid="account-manage-tab">
          <div data-testid="account-type-card" className="rounded-3xl border border-[var(--border-subtle)] bg-white/60 p-6 sm:p-8 mb-6">
            <h3 className="font-display font-bold text-lg text-slate-900">{t("kyc.accountType")}</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-lg">{t("acc.typeNote")}</p>
            <div className="mt-4 flex gap-2 max-w-sm">
              {[["private", t("acc.typePrivate")], ["business", t("acc.typeBusiness")]].map(([k, label]) => {
                const active = (user.kyc_account_type || "private") === k;
                // azienda è definitiva: il pulsante "Privato" si disattiva per sempre
                const lockedOff = user.kyc_account_type === "business" && k === "private";
                return (
                  <button key={k} data-testid={`account-type-${k}`} onClick={() => changeAccountType(k)} disabled={typeBusy || lockedOff}
                    className={`flex-1 rounded-full px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${lockedOff ? "cursor-not-allowed" : ""} ${active ? "bg-[#FF6B35] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            {user.kyc_account_type === "business" && (
              <p data-testid="account-type-locked" className="mt-3 font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
                {t("acc.typeLockedNote")}
              </p>
            )}
            {user.kyc_account_type === "business" && !user.kyc_done && (
              <div data-testid="account-type-warning" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                ⚠ {t("acc.typeIncomplete")}
              </div>
            )}
          </div>
          <div data-testid="fiscal-data-card" className="rounded-3xl border border-[var(--border-subtle)] bg-white/60 p-6 sm:p-8 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900">{t("acc.fiscalData")}</h3>
                <p className="mt-1 text-sm text-slate-500">{t("acc.fiscalDataDesc")}</p>
              </div>
              <button data-testid="edit-fiscal-button" onClick={() => setEditKycOpen(true)}
                className="rounded-full bg-[#FF6B35] text-white px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors">
                {t("acc.editFiscal")}
              </button>
            </div>
          </div>
          <IdentityVerificationCard onChanged={checkAuth} />
          {user.kyc_account_type === "business" && !user.is_dealer && <ApiAccessCard onChanged={checkAuth} />}
        </div>
      )}
      {tab === "security" && (
        <div className="mt-10" data-testid="account-security-tab">
          <TwoFactorCard />
          <PhoneVerifyCard />
          <TrustedDevicesCard />
          <SecurityCard />
          <PrivacySection user={user} onChanged={checkAuth} />
        </div>
      )}
      <KycModal open={editKycOpen} onOpenChange={setEditKycOpen} onDone={() => { checkAuth(); toast.success(t("admin.saved")); }} />
    </div>
  );
}
